import { View } from "@pimo/pimo-app-builder";
import {
  DragAndDropLayout,
  PMBarChartCard,
  PMDonutChartCard,
} from "@pimo/pimo-components";
import { ReportingItem } from "vmd-types";

import { TestAppState } from "../../../app";
import { DONUT_CARD_LAYOUT, DONUT_CHART_COLORS } from "../../../constants";
import { withTotalInTitle } from "../with-total-in-title";

const getCapabilitiesData = (items: ReportingItem[] | undefined) => {
  const capabilityCounts = new Map<string, number>();

  for (const item of items ?? []) {
    const capabilities = item.basicItem?.applicationCapabilities ?? [];
    for (const capability of capabilities) {
      if (!capability) continue;
      capabilityCounts.set(
        capability,
        (capabilityCounts.get(capability) ?? 0) + 1
      );
    }
  }

  const entries = Array.from(capabilityCounts.entries())
    .sort((a, b) => b[1] - a[1])
    .map(([label, count], index) => ({
      label,
      count,
      color: DONUT_CHART_COLORS[index % DONUT_CHART_COLORS.length],
    }));

  return { entries };
};

/** Adds the application-capabilities bar chart and donut chart (#2222). */
export function addCapabilitiesCards(
  view: View<TestAppState, DragAndDropLayout>
) {
  // Application Capabilities
  const capabilitiesBarChartComponent = view.addComponent({
    component: PMBarChartCard,
    layoutProps: {
      componentId: "capabilitiesBarChartComponent",
      labels: ["barChart", "chart", "capability"],
      description: "Shows systems grouped by application capability.",
      title: "Systems per Capability",
    },
  });

  capabilitiesBarChartComponent.mapState(({ currentReport }) => {
    const items = (currentReport?.project?.reportingItems ?? []).filter(
      (item) => !item?.isChannelHeader
    );
    const { entries } = getCapabilitiesData(items);

    return {
      title: "Systems per Capability",
      categories: entries.map((entry) => entry.label),
      data: entries.map((entry) => entry.count),
      chartType: 1 as const,
      xLabel: "Systems",
      yLabel: "Capability",
      colors: entries.map((entry) => entry.color),
      fixedHeight: "320px",
    };
  });

  const capabilitiesDonutChartComponent = view.addComponent({
    component: PMDonutChartCard,
    layoutProps: {
      componentId: "capabilitiesDonutChartComponent",
      labels: ["donutChart", "chart", "capability"],
      description: "Shows systems grouped by application capability.",
      title: "Systems per Capability",
    },
  });

  capabilitiesDonutChartComponent.mapState(({ currentReport }) => {
    const items = (currentReport?.project?.reportingItems ?? []).filter(
      (item) => !item?.isChannelHeader
    );
    const { entries } = getCapabilitiesData(items);

    return {
      title: withTotalInTitle("Systems per Capability", items.length),
      icon: "DonutLarge",
      ...DONUT_CARD_LAYOUT,
      data: entries.map((entry) => ({
        label: entry.label,
        value: entry.count,
        color: entry.color,
      })),
    };
  });
}
